import axios from 'axios';
import React, { useEffect, useState, useMemo, useRef, RefObject } from 'react';
import cn from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import { track } from '@amplitude/analytics-browser';
import {
  GetPostCommentsResult,
  Language,
  PostComment,
  PostType,
  User,
} from 'common/interfaces/api';
import Icon from 'components/common/Icon';
import ThreeDotsMenu from './ThreeDotsMenu';
import { COMMENTS_PER_PAGE, timeSince, useMediaQuery } from 'common/utils';
import useStore from 'store/timeline';
import useGlobalStore from 'store/global';

import styles from './PostComment.module.scss';
import RichContent from 'components/common/RichContent';
import RoundIcon from 'components/common/RoundIcon';
import Link from 'next/link';
import BadgeIcon from 'components/common/BadgeIcon';
import { useInfiniteQuery } from 'react-query';
import CommentBox from 'components/common/CommentBox';
import TippedItem from 'components/common/TippedItem';
import { isIzanaviMember, isMembershipAccount } from 'common/utils/membership';
import { ScreenName } from 'common/utils/pp_tracking';
import MembershipVipIcon from 'components/common/MembershipVipIcon';

export type Props = {
  postId: number;
  postOwner: User;
  comment: PostComment;
  level: number; // only support show reply for level = 0
  isLocked: boolean;
  parentId?: number;
  parentReplyRef?: RefObject<HTMLDivElement>;
  isBlocked?: boolean;
  postType?: PostType;
  tinyMode?: boolean;
};

function PostCommentItem({
  postId,
  comment,
  level,
  postOwner,
  isLocked,
  parentId,
  parentReplyRef,
  isBlocked = false,
  // isOpenForSubscription = true,
  tinyMode = false,
}: Props): JSX.Element {
  const [isLiked, setIsLiked] = useState(comment.is_liked);
  const [likeCount, setLikeCount] = useState(comment.like_count);
  const currentUser = useStore((state) => state.currentUser);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const user = comment.user_info;
  const isOwnPost = currentUser?.id === postOwner.id;
  const isOwnComment = currentUser?.id === user.id;
  const [isFollowed, setIsFollowed] = useState(user.is_followed);
  const refetchPostComments = useStore((state) => state.refetchPostComments);
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyBox, setShowReplyBox] = useState(false);
  const selfLastReplyRef = useRef<HTMLDivElement>();
  const isMobileScreen = useMediaQuery('(max-width: 767px)');
  const setShowMembershipPayDialog = useGlobalStore(
    (state) => state.setShowMembershipPayDialog
  );
  useEffect(() => {
    setIsFollowed(user?.is_followed);
  }, [user?.is_followed]);

  useEffect(() => {
    setIsLiked(comment.is_liked);
    setLikeCount(comment.like_count);
  }, [comment.is_liked, comment.like_count]);

  const shouldRefetchPostComments = useStore(
    (state) => state.shouldRefetchPostComments
  );
  const shouldRefetchRepliesCommentId = useStore(
    (state) => state.shouldRefetchRepliesCommentId
  );
  const { data, fetchNextPage, hasNextPage, refetch } = useInfiniteQuery(
    ['getReplies', comment.id?.toString()],
    async ({ pageParam = 0 }) => {
      if (!showReplies) return { data: [] };
      return (
        await axios.get(
          `/posts/comments/${comment.id}/replies?last_comment_id=${
            pageParam as number
          }`
        )
      ).data as GetPostCommentsResult;
    },
    {
      getNextPageParam: (lastPage) => {
        const comments = lastPage?.data;
        if (comments && comments.length === COMMENTS_PER_PAGE) {
          let lastId = comments[0].id;
          comments.forEach((comment) => {
            if (comment.id < lastId) {
              lastId = comment.id;
            }
          });
          return lastId;
        }
      },
      enabled: level === 0 && !!comment.id && showReplies,
      refetchOnWindowFocus: false,
    }
  );
  const pages = data?.pages;
  const replies: PostComment[] = useMemo(
    () =>
      pages
        ? pages.reduce<PostComment[]>((curr, page) => {
            const comments = page.data;
            return [...comments, ...curr];
          }, [])
        : [],
    [pages]
  );

  useEffect(() => {
    if (
      shouldRefetchPostComments &&
      shouldRefetchRepliesCommentId === comment.id
    ) {
      void refetch();
    }
  }, [
    shouldRefetchPostComments,
    refetch,
    shouldRefetchRepliesCommentId,
    comment.id,
  ]);

  const handleMoreClick = () => {
    if (!currentUser) {
      showLogin({});
      return;
    }
    void fetchNextPage();
  };

  const handleLikeClick = async (commentId: number, isLiked: boolean) => {
    track('Like Comment');
    if (currentUser) {
      const action = isLiked ? 'unlike' : 'like';
      if (isLiked) {
        setIsLiked(false);
        setLikeCount((count) => count - 1);
      } else {
        setIsLiked(true);
        setLikeCount((count) => count + 1);
      }
      await axios.post(
        `/posts/${postId}/comments/${commentId}/reaction?action=${action}`,
        {}
      );
    } else {
      showLogin({});
    }
  };

  const handleFollowClick = async (userId: number, isFollowed: boolean) => {
    if (currentUser) {
      const action = isFollowed ? 'unfollow' : 'follow';
      if (isFollowed) {
        setIsFollowed(false);
      } else {
        setIsFollowed(true);
      }
      await axios.post(`/users/follows`, {
        user_id: userId,
        action: action,
      });
      refetchPostComments(0);
    } else {
      showLogin({});
    }
  };

  const handleShowReplies = () => {
    if (!currentUser) {
      showLogin({});
      return;
    }

    setShowReplies(true);
  };

  const handleReply = () => {
    handleShowReplies();
    if (isBlocked) return;
    setShowReplyBox(true);
  };

  // Footer
  const timeSinceText = timeSince(new Date(comment.created_at), Language.Ja);

  if (tinyMode) {
    return (
      <div
        className={cn(styles.postCommentWrapper, {
          [styles[`tip-${comment?.user_tipping?.tipping_package?.id}`]]:
            !!comment?.user_tipping?.tipping_package,
        })}
      >
        <div className={styles.avatar}>
          <RoundIcon
            user={user}
            width={33}
            height={33}
            enableClick
            hasRing
            emotion={user.emotion}
          />
        </div>
        <div className={cn(styles.contentBox, styles.tinyMode)}>
          <div className={styles.content}>
            <RichContent
              text={comment.text}
              mentionTargets={comment.mentions}
            />
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <div
        className={cn(styles.postCommentWrapper, {
          [styles[`tip-${comment?.user_tipping?.tipping_package?.id}`]]:
            !!comment?.user_tipping?.tipping_package,
        })}
      >
        <div className={styles.avatar}>
          <RoundIcon
            user={user}
            width={35}
            height={35}
            enableClick
            hasRing
            emotion={user.emotion}
          />
        </div>
        <div className={styles.contentBox}>
          <div className={styles.userInfo}>
            <div className={styles.nameJob}>
              <Link href={`/${user.username}`}>
                <a>
                  <div className={styles.userNameText} title={user.name}>
                    {user.name}
                  </div>
                </a>
              </Link>
              <div className={styles.userJobText} title={user.job}>
                {user.job}
              </div>
            </div>
            <div className={styles.badge}>
              <BadgeIcon badge={user.badge} width={34} height={34} />
            </div>
            {(isMembershipAccount(user) || isIzanaviMember(user)) && (
              <div className={styles.membershipIcon}>
                <MembershipVipIcon
                  packageName={
                    user?.membership?.membership_package?.package_name
                  }
                  isIzanavi={isIzanaviMember(user)}
                  width={60}
                  height={34}
                  onClick={() => {
                    setShowMembershipPayDialog({
                      open: true,
                      showMembershipStatus: false,
                      screen: ScreenName.PostDetail,
                    });
                  }}
                />
              </div>
            )}
            {comment?.user_tipping && (
              <span className={styles.tippedItem}>
                <TippedItem
                  tippedInfo={Object.assign({}, comment.user_tipping, {
                    user: comment.user_info,
                  })}
                />
              </span>
            )}
            {!isFollowed && currentUser?.id !== user?.id && (
              <Icon
                name="follow"
                width={32}
                height={32}
                isOn={isFollowed}
                hasOn
                hasHover
                onClick={() => handleFollowClick(user.id, isFollowed)}
              />
            )}
          </div>

          <div className={styles.content}>
            <RichContent
              text={comment.text}
              mentionTargets={comment.mentions}
            />
          </div>
          <div className={styles.footerActions}>
            <div className={styles.timeSince}>{timeSinceText}</div>
            <div className={styles.icon}>
              <Icon
                name="heart"
                width={16}
                height={16}
                isOn={isLiked}
                hasOn
                onClick={() => handleLikeClick(comment.id, isLiked)}
              />
              <div className={cn(styles.number, { [styles.liked]: isLiked })}>
                {likeCount}
              </div>
            </div>
            <div className={styles.icon}>
              <Icon
                name="comment"
                width={16}
                height={16}
                hasOn
                isOn={comment.reply_count > 0}
                onClick={() => handleReply()}
              />
              {comment.reply_count > 0 && (
                <div className={styles.number}>{comment.reply_count}</div>
              )}
            </div>
            {currentUser && (
              <ThreeDotsMenu
                postId={postId}
                commentId={comment.id}
                ownerId={user.id}
                isOwnComment={isOwnComment}
                isOwnPost={isOwnPost}
                parentId={level === 0 ? comment.id : parentId}
              />
            )}
          </div>
          {!isLocked && showReplyBox && (
            <CommentBox
              postId={postId}
              parentId={level === 0 ? comment.id : parentId}
              isReplyBox
              isLocked={isLocked}
              initText={isOwnComment ? '' : `@${comment.user_info.name} `}
              setShowReplyBox={setShowReplyBox}
              callback={() => {
                setShowReplies(true);
                if (isMobileScreen) return;
                if (level === 0) {
                  if (selfLastReplyRef && selfLastReplyRef.current)
                    window.scrollTo(0, selfLastReplyRef.current.offsetTop + 50);
                } else {
                  if (parentReplyRef && parentReplyRef.current)
                    window.scrollTo(0, parentReplyRef.current.offsetTop + 50);
                }
              }}
              mentionTarget={comment.user_info}
            />
          )}
          {level === 0 && comment.reply_count > 0 && !showReplies && (
            <div
              className={styles.repliesTitle}
              onClick={() => handleShowReplies()}
            >
              {comment.reply_count}件の返信を表示
            </div>
          )}
        </div>
      </div>
      {level === 0 && comment.reply_count > 0 && showReplies && (
        <div className={styles.replies}>
          {comment.reply_count > replies.length && hasNextPage && (
            <div className={styles.more} onClick={handleMoreClick}>
              以前のコメントを見る
            </div>
          )}

          <AnimatePresence initial={false}>
            {replies &&
              replies.map((reply) => (
                <motion.div
                  initial={{ opacity: 0, y: -150 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: '70%' }}
                  key={reply.id}
                >
                  <div className={styles.reply} key={reply.id}>
                    <PostCommentItem
                      comment={reply}
                      level={1}
                      postOwner={postOwner}
                      isBlocked={isBlocked}
                      postId={postId}
                      isLocked={isLocked}
                      parentId={comment.id}
                      parentReplyRef={selfLastReplyRef}
                    />
                  </div>
                </motion.div>
              ))}
          </AnimatePresence>
          <div ref={selfLastReplyRef}></div>
        </div>
      )}
    </>
  );
}

const MemoizedPostComment = React.memo(PostCommentItem);
export default MemoizedPostComment;
